import {
  type AssignedWorksheetDetail,
  type AssignedWorksheetDetailListParams,
  type AssignedWorksheetProblemDetail,
  type AssignedWorksheetProblemDetailListParams,
  type GroupAssignedWorksheet,
  type GroupAssignedWorksheetListParams,
  type GroupWorksheetScoring,
  type GroupWorksheetScoringListParams,
  studentWorksheetApi,
  type StudentWorksheetDatetimeBody,
  type WorksheetScoringPayload,
} from '@mathflat/domain/StudentWorksheet'
import {
  GroupStudentWorksheet,
  GroupStudentWorksheetColl,
  GroupStudentWorksheetProblem,
} from 'src/dto'
import type { SchoolGrade } from 'src/dto/Student/SchoolGrade'
import CheckableMap from 'src/models/CheckableMap'

import { mpEventInfo } from '@/constant'
import type { LessonClass, Student, Worksheet } from '@/dto'
import {
  StudentWorksheet,
  StudentWorksheetColl,
  StudentWorksheetProblem,
  StudentWorksheetProblemColl,
} from '@/dto'
import type { EventPropertiesSchema } from '@/utils/trackings/schema/eventProperties.schema'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

/***
 * @see StudentWorksheet
 ***/
class LessonWorksheetController implements Controller {
  adapter = {
    studentWorksheet: makeAdapter({
      from: ({
        assignDatetime,
        scoreDatetime,
        openDatetime,
        creverseShared,
        ...data
      }: AssignedWorksheetDetail) => {
        return new StudentWorksheet({
          ...data,
          creverseShared: creverseShared ?? false,
          assignDatetime: new Date(assignDatetime),
          ...(scoreDatetime && { scoreDatetime: new Date(scoreDatetime) }),
          ...(openDatetime && { openDatetime: new Date(openDatetime) }),
        })
      },
    }),
    groupStudentWorksheet: makeAdapter({
      from: (data: GroupAssignedWorksheet) => {
        return new GroupStudentWorksheet({
          ...data,
          id: data.worksheet.id,
          assignDatetime: new Date(data.assignDatetime),
        })
      },
    }),
    studentWorksheetProblem: makeAdapter({
      from: (data: AssignedWorksheetProblemDetail) => {
        return new StudentWorksheetProblem({
          id: data.worksheetProblemId,
          result: data.result,
          userAnswer: data.userAnswer,
          problemId: data.problem.id,
          index: data.problem.index,
          favorite: data.problem.favorite,
          type: data.problem.type,
          answer: data.problem.answer,
          answerData: data.problem.answerData,
          keypadTypes: data.problem.keypadTypes,
          problemImageUrl: data.problem.problemImageUrl,
          answerImageUrl: data.problem.answerImageUrl,
          solutionImageUrl: data.problem.solutionImageUrl,
          handwrittenNoteUrl: data.handwrittenNoteUrl,
          videoMatched: data.problem.videoMatched,
          video: data.problem.video,
          conceptName: data.problem.conceptName,
          tagTop: data.problem.tagTop,
          level: data.problem.level,
          answerUnits: data.problem.answerUnits,
          conceptId: data.problem.conceptId,
          sourceType: data.problem.sourceType,
        })
      },
    }),
    groupStudentWorksheetProblem: makeAdapter({
      from: (data: GroupWorksheetScoring) => {
        return new GroupStudentWorksheetProblem(data)
      },
    }),
  }

  fetchStudentWorksheets = async (
    studentId: Student['id'],
    params: AssignedWorksheetDetailListParams,
  ) => {
    const { content, last } = await studentWorksheetApi.getAssignedWorksheetDetails(
      studentId,
      params,
      {
        mp_event_name: mpEventInfo.특정학생학습지조회,
      },
    )

    const parseData = content.map(this.adapter.studentWorksheet.from)
    const studentWorksheetMap = CheckableMap.fromArr(parseData, 'id')

    return {
      studentWorksheetColl: new StudentWorksheetColl(studentWorksheetMap),
      isLast: last,
    }
  }
  fetchSpecificStudentWorksheet = async (studentWorksheetId: StudentWorksheet['id']) => {
    const data = await studentWorksheetApi.getAssignedWorksheetDetail(studentWorksheetId, {
      mp_event_name: mpEventInfo.출제된학습지상세조회,
    })

    return this.adapter.studentWorksheet.from(data)
  }

  fetchGradeStudentWorksheets = async (
    schoolType: SchoolGrade['schoolType'],
    grade: SchoolGrade['grade'],
    params: GroupAssignedWorksheetListParams,
  ) => {
    const { content, last, empty } = await studentWorksheetApi.getGradeAssignedWorksheets(
      { schoolType, grade },
      params,
      { mp_event_name: mpEventInfo.학년별출제된학습지조회 },
    )

    const parseData = content.map(this.adapter.groupStudentWorksheet.from)
    const gradeStudentWorksheetMap = CheckableMap.fromArr(parseData, 'id')

    return {
      gradeStudentWorksheetColl: new GroupStudentWorksheetColl(gradeStudentWorksheetMap),
      isLast: last,
      empty,
    }
  }

  fetchClassStudentWorksheets = async (
    lessonClassId: LessonClass['id'],
    params: GroupAssignedWorksheetListParams,
  ) => {
    const { content, last, empty } = await studentWorksheetApi.getLessonClassAssignedWorksheets(
      { lessonClassId },
      params,
      { mp_event_name: mpEventInfo.반별출제된학습지조회 },
    )

    const parseData = content.map(this.adapter.groupStudentWorksheet.from)
    const classStudentWorksheetMap = CheckableMap.fromArr(parseData, 'id')

    return {
      classStudentWorksheetColl: new GroupStudentWorksheetColl(classStudentWorksheetMap),
      isLast: last,
      empty,
    }
  }

  fetchStudentWorksheetProblems = async (
    studentWorksheetId: StudentWorksheet['id'],
    params: AssignedWorksheetProblemDetailListParams,
    signal?: AbortSignal,
  ) => {
    const problems = await studentWorksheetApi.getAssignedWorksheetProblemDetails(
      studentWorksheetId,
      params,
      {
        meta: { mp_event_name: mpEventInfo.출제된학습지문제조회 },
        signal,
      },
    )

    const parseData = problems.map(this.adapter.studentWorksheetProblem.from)
    const studentWorksheetProblemMap = CheckableMap.fromArr(parseData, 'id')

    return {
      studentWorksheetProblemColl: new StudentWorksheetProblemColl(studentWorksheetProblemMap),
    }
  }

  fetchGradeStudentWorksheetProblems = async (
    schoolType: SchoolGrade['schoolType'],
    grade: SchoolGrade['grade'],
    worksheetId: Worksheet['id'],
    params: GroupWorksheetScoringListParams,
    signal?: AbortSignal,
  ) => {
    const { content } = await studentWorksheetApi.getGradeWorksheetScorings(
      { schoolType, grade, worksheetId },
      params,
      {
        meta: { mp_event_name: mpEventInfo.학년별출제된학습지채점조회 },
        signal,
      },
    )

    const parseData = content.map(this.adapter.groupStudentWorksheetProblem.from)

    return {
      gradeStudentWorksheetProblems: this.sortScoringStudents(parseData),
    }
  }

  fetchClassStudentWorksheetProblems = async (
    lessonClassId: LessonClass['id'],
    worksheetId: Worksheet['id'],
    params: GroupWorksheetScoringListParams,
    signal?: AbortSignal,
  ) => {
    const { content } = await studentWorksheetApi.getLessonClassWorksheetScorings(
      { lessonClassId, worksheetId },
      params,
      {
        meta: { mp_event_name: mpEventInfo.반별출제된학습지채점조회 },
        signal,
      },
    )

    const parseData = content.map(this.adapter.groupStudentWorksheetProblem.from)

    return {
      classStudentWorksheetProblems: this.sortScoringStudents(parseData),
    }
  }

  assignStudentWorksheet = async (
    studentIdList: Student['id'][],
    worksheetIdList: Worksheet['id'][],
    trackingProps?: EventPropertiesSchema.출제학습지타입,
  ) => {
    return studentWorksheetApi.assignWorksheets(
      { studentIdList, worksheetIdList },
      { meta: { mp_event_name: mpEventInfo.학습지출제 }, trackingProps },
    )
  }

  patchStudentWorksheet = async (
    studentWorksheetId: StudentWorksheet['id'],
    dateTime: StudentWorksheetDatetimeBody,
  ) => {
    await studentWorksheetApi.updateAssignedWorksheetDatetime(studentWorksheetId, dateTime, {
      mp_event_name: mpEventInfo.학습지출제일채점일수정,
    })
  }

  patchStudentWorksheetScoring = async (
    studentWorksheetId: StudentWorksheet['id'],
    data: WorksheetScoringPayload,
  ) => {
    await studentWorksheetApi.scoreAssignedWorksheet(studentWorksheetId, data, {
      mp_event_name: mpEventInfo.학습지문제당채점,
    })
  }

  deleteStudentWorksheets = async (studentWorksheetIdList: StudentWorksheet['id'][]) =>
    await studentWorksheetApi.deleteAssignedWorksheets(studentWorksheetIdList, {
      mp_event_name: mpEventInfo.학습지출제취소,
    })

  private sortScoringStudents = (studentWorksheetProblems: GroupStudentWorksheetProblem[]) =>
    studentWorksheetProblems.sort((a, b) =>
      a.studentName > b.studentName ? 1 : a.studentName < b.studentName ? -1 : 0,
    )

  fetchVirtualScoringStudentWorksheets = async (
    studentWorksheetIdList: StudentWorksheet['id'][],
  ) => {
    return studentWorksheetApi.getStudentWorksheetVirtualScorings(
      { studentWorksheetIds: studentWorksheetIdList },
      { mp_event_name: mpEventInfo.학년별출제된학습지채점조회 },
    )
  }
}

export const lessonWorksheetController = new LessonWorksheetController()
